import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.25s ease;
`;

const ModalBox = styled.div`
  position: relative;
  width: 90vw;
  max-width: 960px;
  height: 80vh;
  max-height: 700px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${scaleIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (max-width: 600px) {
    width: 96vw;
    height: 85vh;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
  flex-shrink: 0;
`;

const HeaderTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const CloseBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.2);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.35); }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const CtrlBtn = styled.button<{ $active?: boolean }>`
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: ${p => p.$active ? '#4f46e5' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#334155'};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${p => p.$active ? '#4338ca' : '#f1f5f9'}; }
`;

const ZoomLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  min-width: 42px;
  text-align: center;
`;

const ImageArea = styled.div`
  flex: 1;
  overflow: hidden;
  cursor: grab;
  position: relative;
  background: #f1f5f9;
  &:active { cursor: grabbing; }
`;

const MapImg = styled.img<{ $scale: number; $x: number; $y: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: center center;
  transform: translate(-50%, -50%) scale(${p => p.$scale}) translate(${p => p.$x}px, ${p => p.$y}px);
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.1s ease-out;
  user-select: none;
  pointer-events: none;
`;

interface PuneMapModalProps {
  open: boolean;
  onClose: () => void;
}

const PuneMapModal: React.FC<PuneMapModalProps> = ({ open, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });
  const imageAreaRef = useRef<HTMLDivElement>(null);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    setZoom(prev => Math.max(0.5, Math.min(5, prev - e.deltaY * 0.001)));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...pan };
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return;
    const dx = (e.clientX - dragStart.current.x) / zoom;
    const dy = (e.clientY - dragStart.current.y) / zoom;
    setPan({ x: panStart.current.x + dx, y: panStart.current.y + dy });
  }, [dragging, zoom]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setDragging(true);
      dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      panStart.current = { ...pan };
    }
  }, [pan]);

  const handleTouchMoveNative = useCallback((e: TouchEvent) => {
    if (!dragging || e.touches.length !== 1) return;
    e.preventDefault();
    const dx = (e.touches[0].clientX - dragStart.current.x) / zoom;
    const dy = (e.touches[0].clientY - dragStart.current.y) / zoom;
    setPan({ x: panStart.current.x + dx, y: panStart.current.y + dy });
  }, [dragging, zoom]);

  const handleTouchEnd = useCallback(() => setDragging(false), []);

  const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);
  const prevOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Reset zoom/pan only when modal first opens
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
    prevOpenRef.current = open;
  }, [open]);

  // ESC key handler
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Attach wheel & touchmove with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = imageAreaRef.current;
    if (!el || !open) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchmove', handleTouchMoveNative as EventListener, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchmove', handleTouchMoveNative as EventListener);
    };
  }, [open, handleWheel, handleTouchMoveNative]);

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderTitle>📍 Pune GA Map — Advertised Locations</HeaderTitle>
          <CloseBtn onClick={onClose} title="Close (ESC)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </Header>
        <Controls>
          <CtrlBtn onClick={() => setZoom(z => Math.min(z + 0.3, 5))}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            Zoom In
          </CtrlBtn>
          <CtrlBtn onClick={() => setZoom(z => Math.max(z - 0.3, 0.5))}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
            Zoom Out
          </CtrlBtn>
          <CtrlBtn onClick={resetView}>Reset</CtrlBtn>
          <ZoomLabel>{Math.round(zoom * 100)}%</ZoomLabel>
        </Controls>
        <ImageArea
          ref={imageAreaRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <MapImg
            src="/assets/Pune_GA_Map_comp.png"
            alt="Pune GA Map"
            $scale={zoom}
            $x={pan.x}
            $y={pan.y}
            draggable={false}
          />
        </ImageArea>
      </ModalBox>
    </Overlay>
  );
};

export default PuneMapModal;
